import {
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { formatDistanceToNow } from 'date-fns';
import { createPortal } from 'react-dom';
import styled, { css, keyframes } from 'styled-components';

import { paradigmLightBlue } from '@paradigm/design-system/src/colors';
import { getUserId } from '@paradigm/store/src/auth/selectors';

import { loadSessionMessages } from '#/api/terminal/chat-history-storage';
import { ChatSession } from '#/api/terminal/types';
import {
  useAgentActions,
  useAgentState,
} from '#/features/terminal/agent-context';
import {
  useTerminalDrawerActions,
  useTerminalDrawerState,
} from '#/features/terminal/drawer-context';

// Label used for the "unfiled" choice in the move-to submenu. Sessions
// without a `customFolderId` render at the root of the Recents list,
// outside any folder wrapper.
const DEFAULT_FOLDER_LABEL = 'Sessions';

// Sentinel id for the unfiled option in the context-menu "Move to" list.
// Selecting it clears the session's `customFolderId`.
const DEFAULT_FOLDER_ID = '__default__';

export default function RecentsView() {
  const state = useAgentState();
  const actions = useAgentActions();
  const drawerActions = useTerminalDrawerActions();
  const drawerState = useTerminalDrawerState();
  const { activeTab } = drawerState;
  const userId = useSelector(getUserId);
  const userIdStr =
    userId != null && userId !== 0 && String(userId).length > 0
      ? String(userId)
      : null;
  const [searchQuery, setSearchQuery] = useState('');
  // Drives the inline "+ New Folder" → name input flow. When non-null,
  // the input renders above the folder list seeded with this value.
  const [creatingFolderName, setCreatingFolderName] = useState<string | null>(
    null,
  );
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);
  const [confirmingDeleteFolderId, setConfirmingDeleteFolderId] = useState<
    string | null
  >(null);
  const [folderMenuId, setFolderMenuId] = useState<string | null>(null);
  const [folderMenuPos, setFolderMenuPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  // Per-folder collapsed state. Folder ids that appear here with `true`
  // render closed; everything else (including folders the user hasn't
  // interacted with yet) defaults to open.
  const [collapsedFolders, setCollapsedFolders] = useState<
    Record<string, boolean>
  >({});
  const [deepMatches, setDeepMatches] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  // The most recent query that finished its deep-search pass. Derived
  // `isDeepSearching` flips synchronously by comparing this against the
  // live input — so the shimmer paints on the very first render after a
  // keystroke, without waiting for an effect.
  const [searchedQuery, setSearchedQuery] = useState('');

  // Cached lower-cased searchable text per session. Built lazily on the
  // first deep-search trigger and only rebuilt when the session list
  // shape changes — so steady-state typing pays O(1) per keystroke.
  const indexRef = useRef<{
    key: string;
    index: Map<string, string> | null;
  }>({ key: '', index: null });

  // App context is paradigm — Paradex and Paradigm chats are stored in
  // their respective apps (no cross-app sync today), so this list is
  // intentionally flat: show every non-temporary session for the
  // current account, sorted pinned-first then most-recently-updated.
  const trimmedQuery = searchQuery.trim().toLowerCase();

  // Folder names are searchable too: if the query matches a folder
  // name, that folder counts as a hit (rendered with all its chats)
  // even when none of its chats match individually.
  const matchedFolderIds = useMemo(() => {
    const ids = new Set<string>();
    if (trimmedQuery.length === 0) return ids;
    for (const f of state.customFolders) {
      if (f.name.toLowerCase().includes(trimmedQuery)) ids.add(f.id);
    }
    return ids;
  }, [state.customFolders, trimmedQuery]);

  const titleMatches = useMemo(() => {
    return state.sessions.filter((s) => {
      if (s.isTemporary === true) return false;
      if (trimmedQuery.length === 0) return true;
      if (s.title.toLowerCase().includes(trimmedQuery)) return true;
      // A session also matches when its parent folder's name matched —
      // searching the folder name pulls the whole folder into view.
      if (s.customFolderId != null && matchedFolderIds.has(s.customFolderId)) {
        return true;
      }
      return false;
    });
  }, [state.sessions, trimmedQuery, matchedFolderIds]);

  // Deep-search fallback: only fires when the title filter has zero
  // hits and the query is non-empty. The index is built the first time
  // this branch runs in a session, then reused for every subsequent
  // zero-title-match search until the session list changes.
  const sessionsKey = useMemo(
    () => state.sessions.map((s) => `${s.sessionId}:${s.updatedAt}`).join('|'),
    [state.sessions],
  );

  useEffect(() => {
    if (trimmedQuery.length === 0 || titleMatches.length > 0) {
      setDeepMatches(new Set());
      setSearchedQuery(trimmedQuery);
      return;
    }
    if (userIdStr == null) {
      setSearchedQuery(trimmedQuery);
      return;
    }

    // 250ms keeps the shimmer visible long enough to register as a
    // transition (a setTimeout(0) finishes in one frame and looks
    // like nothing happened), and naturally debounces typing.
    const handle = window.setTimeout(() => {
      if (
        indexRef.current.index == null ||
        indexRef.current.key !== sessionsKey
      ) {
        const index = new Map<string, string>();
        for (const s of state.sessions) {
          if (s.isTemporary === true) continue;
          const messages = loadSessionMessages(userIdStr, s.sessionId);
          index.set(
            s.sessionId,
            messages
              .map((m) => m.content)
              .join('\n')
              .toLowerCase(),
          );
        }
        indexRef.current = { key: sessionsKey, index };
      }
      const matches = new Set<string>();
      const built = indexRef.current.index;
      if (built == null) return;
      built.forEach((text: string, sessionId: string) => {
        if (text.includes(trimmedQuery)) matches.add(sessionId);
      });
      setDeepMatches(matches);
      setSearchedQuery(trimmedQuery);
    }, 250);

    return () => window.clearTimeout(handle);
  }, [
    trimmedQuery,
    titleMatches.length,
    userIdStr,
    sessionsKey,
    state.sessions,
  ]);

  // Derived synchronously so it's already true on the first render
  // after a keystroke — no effect-tick delay before the shimmer paints.
  const isDeepSearching =
    trimmedQuery.length > 0 &&
    titleMatches.length === 0 &&
    searchedQuery !== trimmedQuery;

  const displaySessions = useMemo(() => {
    let visible: readonly ChatSession[];
    if (trimmedQuery.length === 0 || titleMatches.length > 0) {
      visible = titleMatches;
    } else if (searchedQuery === trimmedQuery && deepMatches.size > 0) {
      visible = state.sessions.filter(
        (s) => s.isTemporary !== true && deepMatches.has(s.sessionId),
      );
    } else {
      visible = [];
    }
    return [...visible].sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      return b.updatedAt - a.updatedAt;
    });
  }, [state.sessions, trimmedQuery, titleMatches, deepMatches, searchedQuery]);

  const handleNewChat = useCallback(() => {
    // If the active session is already empty (no messages exchanged
    // yet), just focus that one instead of creating another blank
    // chat — saves the user from accumulating zombie "New Chat" rows.
    const active = state.sessions.find(
      (s) => s.sessionId === state.activeSessionId,
    );
    const activeIsEmpty =
      active != null &&
      active.isTemporary !== true &&
      state.messages.length === 0;
    if (activeIsEmpty) {
      drawerActions.setActiveTab('chat');
      return;
    }
    actions.createSession({ folder: 'paradigm' });
    drawerActions.setActiveTab('chat');
  }, [
    actions,
    drawerActions,
    state.activeSessionId,
    state.messages.length,
    state.sessions,
  ]);

  const handleNewTempChat = useCallback(() => {
    actions.createSession({ folder: 'paradigm', isTemporary: true });
    drawerActions.setActiveTab('chat');
  }, [actions, drawerActions]);

  const handleSelectSession = useCallback(
    (sessionId: string) => {
      if (sessionId === state.activeSessionId) {
        drawerActions.setActiveTab('chat');
        return;
      }
      actions.switchSession(sessionId);
      drawerActions.setActiveTab('chat');
    },
    [actions, drawerActions, state.activeSessionId],
  );

  // Group sessions by their effective folder: a custom folder if
  // `customFolderId` matches one of the user's folders, otherwise the
  // default Paradex folder. (Sessions referencing a deleted folder fall
  // back to default — the agent-context `deleteCustomFolder` already
  // strips the field, but defending here keeps the UI sane if persisted
  // state drifts.)
  const customFolderIdSet = useMemo(
    () => new Set(state.customFolders.map((f) => f.id)),
    [state.customFolders],
  );

  const sessionsByFolder = useMemo(() => {
    const groups = new Map<string, ChatSession[]>();
    groups.set(DEFAULT_FOLDER_ID, []);
    for (const f of state.customFolders) groups.set(f.id, []);
    for (const s of displaySessions) {
      const key =
        s.customFolderId != null && customFolderIdSet.has(s.customFolderId)
          ? s.customFolderId
          : DEFAULT_FOLDER_ID;
      groups.get(key)!.push(s);
    }
    return groups;
  }, [displaySessions, state.customFolders, customFolderIdSet]);

  // Folder ordering: pinned first, then most-recently-created on top
  // (newest createdAt wins). Splitting pinned vs unpinned so the
  // inline "+ New Folder" input can slot in between them — pinned
  // items stay above; unpinned folders + chats sit below.
  const sortedCustomFolders = useMemo(
    () =>
      [...state.customFolders].sort((a, b) => {
        const aPinned = a.pinned === true;
        const bPinned = b.pinned === true;
        if (aPinned !== bPinned) return aPinned ? -1 : 1;
        return b.createdAt - a.createdAt;
      }),
    [state.customFolders],
  );
  // While searching, hide folders that have neither a name match nor
  // any matching session inside. Empty/unmatched folders during a
  // search would just be noise.
  const isSearchActive = trimmedQuery.length > 0;
  const visibleFolders = useMemo(() => {
    if (!isSearchActive) return sortedCustomFolders;
    return sortedCustomFolders.filter(
      (f) =>
        matchedFolderIds.has(f.id) ||
        (sessionsByFolder.get(f.id)?.length ?? 0) > 0,
    );
  }, [isSearchActive, sortedCustomFolders, matchedFolderIds, sessionsByFolder]);
  const pinnedFolders = useMemo(
    () => visibleFolders.filter((f) => f.pinned === true),
    [visibleFolders],
  );
  const unpinnedFolders = useMemo(
    () => visibleFolders.filter((f) => f.pinned !== true),
    [visibleFolders],
  );

  // Unfiled sessions render in the "Sessions" area below all folders.
  // Already pinned-first thanks to the `displaySessions` sort, so we
  // don't need to split them — pinning a chat just floats it to the
  // top of this list, never above the folder area.
  const unfiledSessions = sessionsByFolder.get(DEFAULT_FOLDER_ID) ?? [];

  const handleStartCreatingFolder = useCallback(() => {
    setCreatingFolderName('');
  }, []);

  const handleCommitNewFolder = useCallback(() => {
    if (creatingFolderName == null) return;
    const trimmed = creatingFolderName.trim();
    if (trimmed.length > 0) {
      actions.createCustomFolder(trimmed);
    }
    setCreatingFolderName(null);
  }, [actions, creatingFolderName]);

  const handleCancelNewFolder = useCallback(() => {
    setCreatingFolderName(null);
  }, []);

  const handleStartRenameFolder = useCallback((folderId: string) => {
    setRenamingFolderId(folderId);
  }, []);

  const handleCommitRenameFolder = useCallback(
    (folderId: string, name: string) => {
      const trimmed = name.trim();
      if (trimmed.length > 0) {
        actions.renameCustomFolder(folderId, trimmed);
      }
      setRenamingFolderId(null);
    },
    [actions],
  );

  // List of folders shown in the session context-menu's "Move to" view.
  // Includes the default Paradex folder so users can move a chat back
  // out of a custom folder.
  const moveTargets = useMemo(
    () => [
      { id: DEFAULT_FOLDER_ID, name: DEFAULT_FOLDER_LABEL },
      ...state.customFolders.map((f) => ({ id: f.id, name: f.name })),
    ],
    [state.customFolders],
  );

  const handleMoveSession = useCallback(
    (sessionId: string, folderId: string) => {
      actions.setSessionFolder(
        sessionId,
        folderId === DEFAULT_FOLDER_ID ? undefined : folderId,
      );
    },
    [actions],
  );

  const toggleFolderCollapsed = useCallback((folderId: string) => {
    setCollapsedFolders((prev) => ({
      ...prev,
      // Treat unset (`undefined`) as expanded → next state is collapsed.
      [folderId]: prev[folderId] !== true,
    }));
  }, []);

  // Folders auto-expand while the user is searching so matches in any
  // folder are visible without an extra click.
  const isSearching = trimmedQuery.length > 0;
  const isFolderOpen = useCallback(
    (folderId: string) => isSearching || collapsedFolders[folderId] !== true,
    [isSearching, collapsedFolders],
  );

  // The "nothing to show" branch must also account for folder-name
  // matches: searching for an empty folder by name should still
  // surface the folder row instead of the empty-state hint.
  const hasNoVisibleContent =
    displaySessions.length === 0 &&
    visibleFolders.length === 0 &&
    creatingFolderName == null;

  // Render a custom-folder section. Hoisted into a closure so the
  // pinned and unpinned passes don't duplicate ~80 lines of JSX.
  const renderFolderSection = (
    folder: import('#/api/terminal/types').CustomFolder,
  ) => {
    const folderSessions = sessionsByFolder.get(folder.id) ?? [];
    const isRenaming = renamingFolderId === folder.id;
    const open = isFolderOpen(folder.id);
    const isConfirmingDelete = confirmingDeleteFolderId === folder.id;
    const isMenuOpen = folderMenuId === folder.id;
    return (
      <FolderSection key={folder.id}>
        <FolderRow
          $open={open}
          onClick={() => {
            if (isRenaming) return;
            toggleFolderCollapsed(folder.id);
          }}
        >
          {folder.pinned === true && <PinIcon />}
          <FolderIcon />
          {isRenaming ? (
            <FolderInlineRename
              initialValue={folder.name}
              onCommit={(name) => handleCommitRenameFolder(folder.id, name)}
              onCancel={() => setRenamingFolderId(null)}
            />
          ) : (
            <FolderLabel
              onDoubleClick={(e) => {
                e.stopPropagation();
                handleStartRenameFolder(folder.id);
              }}
            >
              {folder.name}
            </FolderLabel>
          )}
          <FolderCount>{folderSessions.length}</FolderCount>
          {isConfirmingDelete ? (
            <ConfirmDelete
              onConfirm={(e) => {
                e.stopPropagation();
                actions.deleteCustomFolder(folder.id);
                setConfirmingDeleteFolderId(null);
              }}
              onCancel={(e) => {
                e.stopPropagation();
                setConfirmingDeleteFolderId(null);
              }}
            />
          ) : (
            <FolderEllipsisButton
              type="button"
              aria-label="Folder actions"
              onClick={(e) => {
                e.stopPropagation();
                const rect = (
                  e.currentTarget as HTMLElement
                ).getBoundingClientRect();
                setFolderMenuId(folder.id);
                setFolderMenuPos({
                  x: rect.right - 140,
                  y: rect.bottom,
                });
              }}
            >
              ⋯
            </FolderEllipsisButton>
          )}
        </FolderRow>
        {isMenuOpen && folderMenuPos != null ? (
          <FolderContextMenu
            x={folderMenuPos.x}
            y={folderMenuPos.y}
            pinned={folder.pinned === true}
            onClose={() => setFolderMenuId(null)}
            onPin={() => {
              actions.pinCustomFolder(folder.id, !(folder.pinned === true));
              setFolderMenuId(null);
            }}
            onRename={() => {
              handleStartRenameFolder(folder.id);
              setFolderMenuId(null);
            }}
            onDelete={() => {
              setConfirmingDeleteFolderId(folder.id);
              setFolderMenuId(null);
            }}
          />
        ) : null}
        {open &&
          (folderSessions.length > 0 ? (
            <FolderSessionList>
              {folderSessions.map((s) => (
                <SessionRow
                  key={s.sessionId}
                  session={s}
                  active={s.sessionId === state.activeSessionId}
                  searchQuery={trimmedQuery}
                  moveTargets={moveTargets}
                  onSelect={handleSelectSession}
                  onRename={actions.renameSession}
                  onPin={actions.pinSession}
                  onDelete={actions.deleteSession}
                  onMove={handleMoveSession}
                />
              ))}
            </FolderSessionList>
          ) : (
            <FolderEmpty>No chats yet</FolderEmpty>
          ))}
      </FolderSection>
    );
  };

  return (
    <Container>
      <Header>
        <PrimaryButton type="button" onClick={handleNewChat}>
          <ButtonIconSlot>
            <NewChatIcon />
          </ButtonIconSlot>
          New Chat
        </PrimaryButton>
        <PrimaryButton type="button" onClick={handleStartCreatingFolder}>
          <ButtonIconSlot>
            <FolderIcon />
          </ButtonIconSlot>
          New Folder
        </PrimaryButton>
        <TempChatButton
          type="button"
          onClick={handleNewTempChat}
          title="Start a one-off chat that won't be saved in Recents"
        >
          <ButtonIconSlot>
            <TempChatIcon />
          </ButtonIconSlot>
          Private Chat
        </TempChatButton>
        <PrimaryButton
          type="button"
          $active={activeTab === 'examples'}
          onClick={() => drawerActions.setActiveTab('examples')}
        >
          <ButtonIconSlot>
            <ExamplesIcon />
          </ButtonIconSlot>
          Examples
        </PrimaryButton>
        <PrimaryButton
          type="button"
          $active={activeTab === 'cron'}
          onClick={() => drawerActions.setActiveTab('cron')}
        >
          <ButtonIconSlot>
            <TasksIcon />
          </ButtonIconSlot>
          Tasks
        </PrimaryButton>
      </Header>
      <SearchBar>
        <SearchInput
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search chats"
          aria-label="Search chats"
        />
      </SearchBar>
      <ScrollArea>
        {hasNoVisibleContent ? (
          <EmptyHint>
            {isDeepSearching ? (
              <SearchingShimmer>Searching chat contents…</SearchingShimmer>
            ) : searchQuery.trim().length > 0 ? (
              <>No chats match &ldquo;{searchQuery.trim()}&rdquo;.</>
            ) : (
              <>
                No chats yet — click <em>+ New Chat</em> to start one.
              </>
            )}
          </EmptyHint>
        ) : (
          <>
            {/* Pinned folders, with the pin icon next to the folder. */}
            {pinnedFolders.map(renderFolderSection)}
            {/* Inline "+ New Folder" input lands just below pinned
                folders so newly-created folders surface above the rest. */}
            {creatingFolderName != null && (
              <FolderRow>
                <FolderIcon />
                <FolderInlineInput
                  // The input is created on demand when the user clicks
                  // "+ New Folder"; auto-focusing follows the same
                  // pattern as the session rename input below.
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  value={creatingFolderName}
                  placeholder="Folder name"
                  onChange={(e) => setCreatingFolderName(e.target.value)}
                  onBlur={handleCommitNewFolder}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCommitNewFolder();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      handleCancelNewFolder();
                    }
                  }}
                />
              </FolderRow>
            )}
            {/* Unpinned folders next, newest first. The last folder's
                `border-bottom` doubles as the separator to the unfiled
                chats list below, so no extra divider is rendered here. */}
            {unpinnedFolders.map(renderFolderSection)}
            {/* Unfiled "Sessions" — pinned ones rise to the top of this
                list (not above folders). `unfiledSessions` is already
                sorted pinned-first by `displaySessions`. */}
            {unfiledSessions.length > 0 && (
              <SessionList>
                {unfiledSessions.map((s) => (
                  <SessionRow
                    key={s.sessionId}
                    session={s}
                    active={s.sessionId === state.activeSessionId}
                    searchQuery={trimmedQuery}
                    moveTargets={moveTargets}
                    onSelect={handleSelectSession}
                    onRename={actions.renameSession}
                    onPin={actions.pinSession}
                    onDelete={actions.deleteSession}
                    onMove={handleMoveSession}
                  />
                ))}
              </SessionList>
            )}
          </>
        )}
      </ScrollArea>
    </Container>
  );
}

interface FolderInlineRenameProps {
  readonly initialValue: string;
  readonly onCommit: (name: string) => void;
  readonly onCancel: () => void;
}

function FolderInlineRename({
  initialValue,
  onCommit,
  onCancel,
}: FolderInlineRenameProps) {
  const [draft, setDraft] = useState(initialValue);
  return (
    <FolderInlineInput
      // Inline rename mounts only on user intent (double-click /
      // context-menu "Rename"); auto-focusing avoids a redundant click.
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => onCommit(draft)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onCommit(draft);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
      }}
    />
  );
}

function FolderIcon() {
  return (
    <FolderIconSvg viewBox="0 0 20 17" fill="none" aria-hidden="true">
      <path
        d="M0.5 9.5V8.5C0.5 7.39543 1.39543 6.5 2.5 6.5H17.5C18.6046 6.5 19.5 7.39543 19.5 8.5V9.5M2.5 0.5H7.09194C7.60367 0.5 8.09594 0.696154 8.46743 1.04809L9.42146 1.95191C9.79295 2.30385 10.2852 2.5 10.7969 2.5H17.5C18.6046 2.5 19.5 3.39543 19.5 4.5V14.5C19.5 15.6046 18.6046 16.5 17.5 16.5H2.5C1.39543 16.5 0.5 15.6046 0.5 14.5V2.5C0.5 1.39543 1.39543 0.5 2.5 0.5Z"
        stroke="currentColor"
        strokeLinejoin="round"
      />
    </FolderIconSvg>
  );
}

function NewChatIcon() {
  return (
    <NewChatIconSvg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M16.2426 12.0005H7.75736M12 16.2431V7.75781M21.25 12C21.25 17.1086 17.1086 21.25 12 21.25C6.89137 21.25 2.75 17.1086 2.75 12C2.75 6.89137 6.89137 2.75 12 2.75C17.1086 2.75 21.25 6.89137 21.25 12Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </NewChatIconSvg>
  );
}

function ExamplesIcon() {
  // Lightbulb outline — represents the catalog of example prompts.
  return (
    <SidebarNavIconSvg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M10 2.5C7.24 2.5 5 4.74 5 7.5C5 9.16 5.81 10.63 7.06 11.55C7.42 11.82 7.5 12.15 7.5 12.5V13.5H12.5V12.5C12.5 12.15 12.58 11.82 12.94 11.55C14.19 10.63 15 9.16 15 7.5C15 4.74 12.76 2.5 10 2.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M8 15.5H12M8.75 17H11.25"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </SidebarNavIconSvg>
  );
}

function TasksIcon() {
  // Checkbox with a checkmark — represents scheduled tasks (cron).
  return (
    <SidebarNavIconSvg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect
        x="3"
        y="3"
        width="14"
        height="14"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M6.5 10L9 12.5L13.5 7.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </SidebarNavIconSvg>
  );
}

interface SessionRowProps {
  readonly session: ChatSession;
  readonly active: boolean;
  readonly searchQuery: string;
  readonly moveTargets: ReadonlyArray<{ id: string; name: string }>;
  readonly onSelect: (sessionId: string) => void;
  readonly onRename: (sessionId: string, title: string) => void;
  readonly onPin: (sessionId: string, pinned: boolean) => void;
  readonly onDelete: (sessionId: string) => void;
  readonly onMove: (sessionId: string, folderId: string) => void;
}

function SessionRow({
  session,
  active,
  searchQuery,
  moveTargets,
  onSelect,
  onRename,
  onPin,
  onDelete,
  onMove,
}: SessionRowProps) {
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState(session.title);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const closeMenu = useCallback(() => setMenu(null), []);

  useEffect(() => {
    if (menu == null) return;
    function onClickAway(e: globalThis.MouseEvent) {
      const target = e.target as HTMLElement;
      if (target.closest('[data-context-menu]') != null) return;
      closeMenu();
    }
    function onEsc(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') closeMenu();
    }
    window.addEventListener('mousedown', onClickAway, true);
    window.addEventListener('keydown', onEsc, true);
    return () => {
      window.removeEventListener('mousedown', onClickAway, true);
      window.removeEventListener('keydown', onEsc, true);
    };
  }, [menu, closeMenu]);

  useEffect(() => {
    if (!renaming) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [renaming]);

  const startRename = useCallback(() => {
    setRenameDraft(session.title);
    setRenaming(true);
    closeMenu();
  }, [session.title, closeMenu]);

  const commitRename = useCallback(() => {
    const trimmed = renameDraft.trim();
    if (trimmed.length > 0 && trimmed !== session.title) {
      onRename(session.sessionId, trimmed);
    }
    setRenaming(false);
  }, [renameDraft, session.sessionId, session.title, onRename]);

  const cancelRename = useCallback(() => {
    setRenameDraft(session.title);
    setRenaming(false);
  }, [session.title]);

  const handleRowClick = useCallback(() => {
    if (renaming || confirmingDelete) return;
    onSelect(session.sessionId);
  }, [renaming, confirmingDelete, session.sessionId, onSelect]);

  const handleContextMenu = useCallback((e: MouseEvent) => {
    e.preventDefault();
    setMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const handleRenameKey = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitRename();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancelRename();
      }
    },
    [commitRename, cancelRename],
  );

  const handleEllipsis = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    // Anchor the menu's top-right at the button's bottom-right so it
    // opens leftward (and downward) — keeps it visually attached to
    // the dots button and inside the terminal panel rather than
    // spilling rightward into the page underneath when the drawer
    // is floating or docked to the right edge.
    const approxMenuWidth = 140;
    setMenu({ x: rect.right - approxMenuWidth, y: rect.bottom });
  }, []);

  const relativeTime = useMemo(() => {
    try {
      return formatDistanceToNow(session.updatedAt, { addSuffix: true });
    } catch {
      return '';
    }
  }, [session.updatedAt]);

  return (
    <Row
      $active={active}
      onClick={handleRowClick}
      onContextMenu={handleContextMenu}
    >
      {session.pinned ? (
        <PinSlot>
          <PinIcon />
        </PinSlot>
      ) : null}
      <RowBody>
        {renaming ? (
          <RenameInput
            ref={inputRef}
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            onKeyDown={handleRenameKey}
            onBlur={commitRename}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <RowTitle $dim={searchQuery.length > 0} title={session.title}>
            <HighlightedTitle title={session.title} query={searchQuery} />
          </RowTitle>
        )}
        <RowMeta>{relativeTime}</RowMeta>
      </RowBody>
      {confirmingDelete ? (
        <ConfirmDelete
          onConfirm={(e) => {
            e.stopPropagation();
            onDelete(session.sessionId);
          }}
          onCancel={(e) => {
            e.stopPropagation();
            setConfirmingDelete(false);
          }}
        />
      ) : (
        <EllipsisButton
          type="button"
          aria-label="Session actions"
          onClick={handleEllipsis}
        >
          ⋯
        </EllipsisButton>
      )}

      {menu != null ? (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          pinned={session.pinned}
          moveTargets={moveTargets}
          currentFolderId={session.customFolderId}
          onPin={() => {
            onPin(session.sessionId, !session.pinned);
            closeMenu();
          }}
          onRename={startRename}
          onMove={(folderId) => {
            onMove(session.sessionId, folderId);
            closeMenu();
          }}
          onDelete={() => {
            setConfirmingDelete(true);
            closeMenu();
          }}
        />
      ) : null}
    </Row>
  );
}

/**
 * Pin glyph mirroring the title-bar Pin button in `TerminalDrawer.tsx` so
 * the two share the same visual language.
 */
function PinIcon() {
  return (
    <PinIconSvg viewBox="0 0 24 24" fill="none" aria-label="Pinned">
      <path
        d="M19.8011 9.61919C19.9139 9.50626 19.9773 9.35313 19.9773 9.19346C19.9773 9.0338 19.9139 8.88067 19.8011 8.76773L15.2343 4.19872C15.1214 4.08586 14.9682 4.02246 14.8086 4.02246C14.6489 4.02246 14.4958 4.08586 14.3829 4.19872L10.0691 8.52456C10.0691 8.52456 7.97992 7.48037 5.74849 9.28117C5.68232 9.33413 5.62808 9.40047 5.5893 9.47584C5.55053 9.55121 5.5281 9.63391 5.52348 9.71854C5.51886 9.80317 5.53216 9.88783 5.5625 9.96697C5.59284 10.0461 5.63954 10.118 5.69956 10.1778L13.8227 18.3002C13.8836 18.3606 13.9566 18.4073 14.0369 18.4372C14.1173 18.4671 14.2031 18.4796 14.2886 18.4736C14.3741 18.4677 14.4574 18.4436 14.5328 18.4029C14.6083 18.3623 14.6742 18.3059 14.7261 18.2377C15.3578 17.3976 16.35 15.6713 15.4873 13.9465L19.8011 9.61919Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.76036 14.2393L6.12488 17.8747"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </PinIconSvg>
  );
}

function ContextMenu({
  x,
  y,
  pinned,
  moveTargets,
  currentFolderId,
  onPin,
  onRename,
  onMove,
  onDelete,
}: {
  readonly x: number;
  readonly y: number;
  readonly pinned: boolean;
  readonly moveTargets: ReadonlyArray<{ id: string; name: string }>;
  readonly currentFolderId: string | undefined;
  readonly onPin: () => void;
  readonly onRename: () => void;
  readonly onMove: (folderId: string) => void;
  readonly onDelete: () => void;
}) {
  const [view, setView] = useState<'main' | 'move'>('main');
  // Render through a portal to document.body so the menu escapes any
  // ancestor that establishes a containing block for fixed positioning
  // (the floating terminal panel uses `backdrop-filter`, which makes
  // `position: fixed` resolve against the panel and `overflow: hidden`
  // clip the menu otherwise — exact bug the user hit when unpinned).
  const ref = useRef<HTMLDivElement>(null);
  const [clamped, setClamped] = useState<{ x: number; y: number }>(() =>
    initialClamp(x, y),
  );

  // Re-clamp synchronously after layout (before paint) so the user never
  // sees the menu pop in at the unclamped position. The docked panel
  // hugs the right edge, so the unflipped menu usually overflows — this
  // is the bug where the dropdown landed outside the view.
  useLayoutEffect(() => {
    const el = ref.current;
    if (el == null) return;
    const rect = el.getBoundingClientRect();
    const margin = 8;
    const maxX = window.innerWidth - rect.width - margin;
    const maxY = window.innerHeight - rect.height - margin;
    setClamped({
      x: Math.max(margin, Math.min(x, maxX)),
      y: Math.max(margin, Math.min(y, maxY)),
    });
  }, [x, y]);

  // The chat's current folder is hidden from the Move-to list entirely
  // — there's no use case for "move to where I already am" so we keep
  // the dropdown to actionable targets only. Unfiled sessions map to
  // the synthetic default folder id.
  const effectiveCurrentFolderId = currentFolderId ?? DEFAULT_FOLDER_ID;
  const visibleMoveTargets = moveTargets.filter(
    (t) => t.id !== effectiveCurrentFolderId,
  );

  return createPortal(
    <MenuPositioned
      ref={ref}
      data-context-menu
      style={{ left: clamped.x, top: clamped.y }}
    >
      {view === 'main' ? (
        <>
          <MenuItem
            type="button"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              onPin();
            }}
          >
            {pinned ? 'Unpin' : 'Pin'}
          </MenuItem>
          <MenuItem
            type="button"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              onRename();
            }}
          >
            Rename
          </MenuItem>
          <MenuItem
            type="button"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              setView('move');
            }}
          >
            Move to Folder
          </MenuItem>
          <MenuItem
            $danger
            type="button"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            Delete
          </MenuItem>
        </>
      ) : (
        <>
          <MenuItem
            type="button"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              setView('main');
            }}
          >
            ← Back
          </MenuItem>
          {visibleMoveTargets.map((t) => (
            <MenuItem
              key={t.id}
              type="button"
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onMove(t.id);
              }}
            >
              {t.name}
            </MenuItem>
          ))}
        </>
      )}
    </MenuPositioned>,
    document.body,
  );
}

function FolderContextMenu({
  x,
  y,
  pinned,
  onClose,
  onPin,
  onRename,
  onDelete,
}: {
  readonly x: number;
  readonly y: number;
  readonly pinned: boolean;
  readonly onClose: () => void;
  readonly onPin: () => void;
  readonly onRename: () => void;
  readonly onDelete: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [clamped, setClamped] = useState<{ x: number; y: number }>(() =>
    initialClamp(x, y),
  );

  useEffect(() => {
    function onClickAway(e: globalThis.MouseEvent) {
      const target = e.target as HTMLElement;
      if (target.closest('[data-context-menu]') != null) return;
      onClose();
    }
    function onEsc(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('mousedown', onClickAway, true);
    window.addEventListener('keydown', onEsc, true);
    return () => {
      window.removeEventListener('mousedown', onClickAway, true);
      window.removeEventListener('keydown', onEsc, true);
    };
  }, [onClose]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el == null) return;
    const rect = el.getBoundingClientRect();
    const margin = 8;
    const maxX = window.innerWidth - rect.width - margin;
    const maxY = window.innerHeight - rect.height - margin;
    setClamped({
      x: Math.max(margin, Math.min(x, maxX)),
      y: Math.max(margin, Math.min(y, maxY)),
    });
  }, [x, y]);

  return createPortal(
    <MenuPositioned
      ref={ref}
      data-context-menu
      style={{ left: clamped.x, top: clamped.y }}
    >
      <MenuItem
        type="button"
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          onPin();
        }}
      >
        {pinned ? 'Unpin' : 'Pin'}
      </MenuItem>
      <MenuItem
        type="button"
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          onRename();
        }}
      >
        Rename
      </MenuItem>
      <MenuItem
        $danger
        type="button"
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          onDelete();
        }}
      >
        Delete
      </MenuItem>
    </MenuPositioned>,
    document.body,
  );
}

/**
 * Best-effort initial clamp before the menu measures itself. Assumes a
 * ~140px×120px menu so the first paint already lands inside the viewport
 * even though the precise size depends on font rendering.
 */
function initialClamp(x: number, y: number): { x: number; y: number } {
  const margin = 8;
  const approxW = 140;
  const approxH = 120;
  if (typeof window === 'undefined') return { x, y };
  return {
    x: Math.max(margin, Math.min(x, window.innerWidth - approxW - margin)),
    y: Math.max(margin, Math.min(y, window.innerHeight - approxH - margin)),
  };
}

function ConfirmDelete({
  onConfirm,
  onCancel,
}: {
  readonly onConfirm: (e: MouseEvent) => void;
  readonly onCancel: (e: MouseEvent) => void;
}) {
  return (
    <ConfirmRow>
      <ConfirmText>Delete?</ConfirmText>
      <ConfirmButton type="button" onClick={onConfirm}>
        Yes
      </ConfirmButton>
      <ConfirmCancel type="button" onClick={onCancel}>
        No
      </ConfirmCancel>
    </ConfirmRow>
  );
}

// --- styling ---

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  color: var(--text-primary);
  font-size: 12px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 2px;
  padding: 8px 8px 8px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--divider-color);
`;

const PrimaryButton = styled.button<{ $active?: boolean }>`
  width: 100%;
  height: 30px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  border-radius: 6px;
  border: none;
  background: ${(p) =>
    p.$active === true
      ? 'var(--hover-bg, rgba(255, 255, 255, 0.08))'
      : 'transparent'};
  color: ${(p) => (p.$active === true ? '#fff' : 'var(--text-primary)')};
  font-size: 12px;
  font-weight: ${(p) => (p.$active === true ? 600 : 400)};
  text-align: left;
  font-family: inherit;
  cursor: pointer;

  &:hover {
    background: var(--hover-bg, rgba(255, 255, 255, 0.08));
    color: #fff;
  }
`;

const TempChatButton = styled.button`
  width: 100%;
  height: 30px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 400;
  font-family: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: var(--hover-bg);
  }
`;

function TempChatIcon() {
  return (
    <TempChatIconSvg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M10 6.5V10.75L12.75 13.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="10"
        cy="10"
        r="9.25"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeDasharray="3 6"
      />
    </TempChatIconSvg>
  );
}

const TempChatIconSvg = styled.svg`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
`;

// Uniform leading-icon slot so the three top-of-sidebar buttons
// (New Chat, New Folder, Private Chat) align their text labels to
// the same x position regardless of which glyph they use.
const ButtonIconSlot = styled.span`
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

// "+" reads thin and small at the button's base 12px font-size; bump
// the size and tighten line-height so the glyph fills the 14px slot
// at roughly the same visual weight as the folder / clock icons.
const NewChatIconSvg = styled.svg`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: var(--text-secondary);
`;

// Shared wrapper for the Examples / Tasks navigation icons in the
// sidebar — sized to match the chat-bubble icon (16px).
const SidebarNavIconSvg = styled.svg`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: var(--text-secondary);
`;

const SearchBar = styled.div`
  flex-shrink: 0;
  padding: 8px 12px;
  border-bottom: 1px solid var(--divider-color);
`;

const SearchInput = styled.input`
  width: 100%;
  height: 32px;
  padding: 6px 10px;
  border: 1px solid var(--border-color, rgba(255, 255, 255, 0.12));
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.04);
  color: #fff;
  font-family: inherit;
  font-size: 12px;

  &::placeholder {
    color: var(--text-secondary);
    opacity: 0.7;
  }

  &:focus {
    outline: none;
    border-color: ${paradigmLightBlue};
    background: rgba(255, 255, 255, 0.06);
  }
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 4px 12px;
`;

const SessionList = styled.div`
  display: flex;
  flex-direction: column;
`;

// Sessions inside a folder render indented with a faint left rail so
// the user can see at a glance which rows belong to the folder above
// and which are unfiled at the root.
const FolderSessionList = styled(SessionList)`
  margin-left: 18px;
  padding-left: 8px;
  border-left: 1px solid var(--divider-color);
`;

// Each folder gets a subtle separator below it so consecutive folders
// don't run into one another. The last folder's border doubles as the
// separator to the unfiled chats list — no extra SectionDivider needed
// directly below.
const FolderSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px 0;
  border-bottom: 1px solid var(--divider-color, rgba(255, 255, 255, 0.08));
`;

const FolderRow = styled.div<{ $open?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 12px;
  margin-bottom: 4px;
  border-radius: 6px;
  color: ${(p) =>
    p.$open === false ? 'var(--text-label)' : 'var(--text-primary)'};
  font-family: inherit;
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: var(--hover-bg);
    color: var(--text-primary);
  }

  &:hover > button[title='Delete folder'] {
    opacity: 1;
  }
`;

const FolderIconSvg = styled.svg`
  width: 14px;
  height: 12px;
  flex-shrink: 0;
  color: var(--text-secondary);
`;

const FolderLabel = styled.span`
  flex: 1;
  cursor: text;
`;

const FolderInlineInput = styled.input`
  flex: 1;
  padding: 2px 4px;
  border-radius: 4px;
  border: 1px solid var(--accent-color);
  background: var(--bg-color-container);
  color: var(--text-primary);
  font-size: 12px;
  font-family: inherit;
  outline: none;
`;

const FolderEllipsisButton = styled.button`
  border: none;
  background: transparent;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1;
  padding: 2px 6px;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0;
  flex-shrink: 0;
  transition: opacity 0.1s ease;

  ${FolderRow}:hover & {
    opacity: 1;
  }

  &:hover {
    color: var(--text-primary);
    background: var(--hover-bg);
  }

  &:focus-visible {
    opacity: 1;
  }
`;

const FolderCount = styled.span`
  font-size: 10px;
  font-weight: 500;
  color: var(--text-label);
  font-variant-numeric: tabular-nums;
`;

const FolderEmpty = styled.div`
  padding: 4px 12px 8px 28px;
  color: var(--text-label);
  font-size: 11px;
  opacity: 0.7;
`;

const EmptyHint = styled.div`
  padding: 24px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1.5;
`;

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const SearchingShimmer = styled.span`
  background: linear-gradient(
    90deg,
    var(--text-secondary) 0%,
    var(--text-secondary) 35%,
    var(--text-primary) 50%,
    var(--text-secondary) 65%,
    var(--text-secondary) 100%
  );
  background-size: 200% 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  animation: ${shimmer} 2.5s linear infinite;
`;

const Row = styled.div<{ $active: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  margin: 0;
  border-radius: 6px;
  cursor: pointer;
  color: var(--text-primary);

  ${(p) =>
    p.$active &&
    css`
      background: var(--hover-bg);
    `}

  &:hover {
    background: var(--hover-bg);
  }

  &:hover > button[aria-label='Session actions'] {
    opacity: 1;
  }
`;

const PinSlot = styled.div`
  width: 16px;
  display: flex;
  justify-content: center;
  flex-shrink: 0;
`;

const PinIconSvg = styled.svg`
  width: 12px;
  height: 12px;
  color: var(--text-secondary);
  flex-shrink: 0;
`;

const RowBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RowTitle = styled.div<{ $dim: boolean }>`
  font-size: 12px;
  font-weight: 400;
  color: ${(p) => (p.$dim ? 'var(--text-label)' : 'var(--text-primary)')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Highlight = styled.span`
  color: var(--text-primary);
`;

function HighlightedTitle({
  title,
  query,
}: {
  readonly title: string;
  readonly query: string;
}) {
  if (query.length === 0) return <>{title}</>;
  const lowerTitle = title.toLowerCase();
  const segments: Array<{ text: string; matched: boolean }> = [];
  let cursor = 0;
  while (cursor < title.length) {
    const idx = lowerTitle.indexOf(query, cursor);
    if (idx === -1) {
      segments.push({ text: title.slice(cursor), matched: false });
      break;
    }
    if (idx > cursor) {
      segments.push({ text: title.slice(cursor, idx), matched: false });
    }
    segments.push({
      text: title.slice(idx, idx + query.length),
      matched: true,
    });
    cursor = idx + query.length;
  }
  return (
    <>
      {segments.map((s, i) =>
        s.matched ? (
          // Segments are recomputed per render from `title` + `query`;
          // they have no stable id, so the array index is acceptable
          // (the list shape is deterministic for a given title/query).
          // eslint-disable-next-line react/no-array-index-key
          <Highlight key={`m-${i}-${s.text}`}>{s.text}</Highlight>
        ) : (
          s.text
        ),
      )}
    </>
  );
}

const RowMeta = styled.div`
  font-size: 10px;
  font-weight: 400;
  color: var(--text-label);
`;

const RenameInput = styled.input`
  padding: 2px 4px;
  border-radius: 4px;
  border: 1px solid var(--accent-color);
  background: var(--bg-color-container);
  color: var(--text-primary);
  font-size: 12px;
  font-family: inherit;
  outline: none;
  width: 100%;
`;

const EllipsisButton = styled.button`
  border: none;
  background: transparent;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1;
  padding: 2px 6px;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.1s ease;
  flex-shrink: 0;

  &:hover {
    color: var(--text-primary);
    background: var(--hover-bg);
  }

  &:focus-visible {
    opacity: 1;
  }
`;

const ConfirmRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const ConfirmText = styled.span`
  color: var(--text-secondary);
  font-size: 10px;
  margin-right: 2px;
`;

const ConfirmButton = styled.button`
  border: 1px solid var(--error-color, #e25c5c);
  background: transparent;
  color: var(--error-color, #e25c5c);
  font-size: 10px;
  font-weight: 600;
  padding: 2px 14px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: var(--error-color, #e25c5c);
    color: var(--cta-text-color, #fff);
  }
`;

const ConfirmCancel = styled.button`
  border: 1px solid var(--border-color);
  background: transparent;
  color: var(--text-secondary);
  font-size: 10px;
  padding: 2px 14px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
  }
`;

// Match the order-builder Select.Content (8px radius, --bg-color-page,
// rgba(white, 0.5) item text → full white on hover) so the right-click
// menu reads as part of the same visual family. No transitions / no
// slide-in: the menu just appears at its measured position
// (useLayoutEffect ensures the first paint is already clamped).
const MenuPositioned = styled.div`
  position: fixed;
  z-index: 99999;
  min-width: 160px;
  /* Solid color rather than --bg-color-container so the menu pops over
     the sidebar (which uses the same token) instead of blending into
     it. */
  background: #1a2030;
  border: 1px solid var(--border-color, rgba(255, 255, 255, 0.12));
  border-radius: 8px;
  padding: 4px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.55);
`;

const MenuItem = styled.button<{ $danger?: boolean }>`
  text-align: left;
  padding: 6px 8px;
  border-radius: 4px;
  border: none;
  background: transparent;
  color: ${(p) =>
    p.$danger === true ? 'var(--error-color, #e25c5c)' : '#fff'};
  font-size: 0.75rem;
  line-height: 1.125rem;
  cursor: pointer;

  &:hover {
    background: var(--hover-bg);
    color: ${(p) =>
      p.$danger === true ? 'var(--error-color, #e25c5c)' : '#fff'};
  }
`;
